import { AppCard } from '@/frontend/components/AppCard'
import { DashboardLayout } from '@/frontend/components/DashboardLayout'
import { StatsCard } from '@/frontend/components/StatsCard'
import { useSession } from '@/frontend/hooks/useAuth'
import {
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { TbAlertCircle, TbApps, TbChevronRight, TbMessageReport, TbUsers } from 'react-icons/tb'

export const Route = createFileRoute('/dashboard')({
  beforeLoad: async ({ context }) => {
    try {
      const data = await context.queryClient.ensureQueryData({
        queryKey: ['auth', 'session'],
        queryFn: () => fetch('/api/auth/session', { credentials: 'include' }).then((r) => r.json()),
      })
      if (!data?.user) throw redirect({ to: '/login' })
    } catch (e) {
      if (e instanceof Error) throw redirect({ to: '/login' })
      throw e
    }
  },
  component: DashboardPage,
})

function getGreeting() {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function formatTimeAgo(dateStr: string) {
  const diff = new Date().getTime() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const SEVERITY_COLOR: Record<string, string> = {
  OPEN: 'red',
  IN_PROGRESS: 'blue',
  ON_HOLD: 'orange',
  RESOLVED: 'teal',
  RELEASED: 'green',
  CLOSED: 'gray',
}

function formatSeverityLabel(s: string) {
  return s.replace(/_/g, ' ')
}

function DashboardPage() {
  const { data: sessionData } = useSession()
  const user = sessionData?.user

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => fetch('/api/dashboard/stats').then((r) => r.json()),
  })

  const { data: apps, isLoading: appsLoading } = useQuery({
    queryKey: ['apps'],
    queryFn: () => fetch('/api/apps').then((r) => r.json()),
  })

  const { data: recentErrors = [], isLoading: recentErrorsLoading } = useQuery({
    queryKey: ['dashboard', 'recent-errors'],
    queryFn: () => fetch('/api/dashboard/recent-errors').then((r) => r.json()),
  })

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  const firstName = user?.name?.split(' ')[0] ?? user?.name

  return (
    <DashboardLayout>
      <Container size="xl" py="lg">
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={500} tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                {today}
              </Text>
              <Title order={2} className="gradient-text">
                {getGreeting()}, {firstName}.
              </Title>
              <Text size="sm" c="dimmed">
                Here's a real-time overview of all your monitored applications.
              </Text>
            </Stack>
            <Button
              variant="gradient"
              gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
              leftSection={<TbApps size={18} />}
              radius="md"
              component={Link}
              to="/apps"
              size="sm"
            >
              Manage Apps
            </Button>
          </Group>

          {statsLoading ? (
            <Loader size="xl" type="dots" mx="auto" mt="xl" />
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <StatsCard
                title="Applications"
                value={stats?.totalApps ?? 0}
                description="Registered platforms"
                icon={TbApps}
                color="brand-blue"
              />
              <StatsCard
                title="Open Errors"
                value={stats?.newErrors ?? 0}
                description="Unresolved bug reports"
                icon={TbMessageReport}
                color="red"
              />
              <StatsCard
                title="Operators"
                value={stats?.activeUsers ?? 0}
                description="Active platform users"
                icon={TbUsers}
                color="teal"
              />
            </SimpleGrid>
          )}

          <Group justify="space-between" align="flex-end" mt="md">
            <Stack gap={2}>
              <Title order={3}>Registered Applications</Title>
              <Text size="sm" c="dimmed">All monitored apps on this platform.</Text>
            </Stack>
            <Button
              variant="subtle"
              color="brand-blue"
              rightSection={<TbChevronRight size={16} />}
              component={Link}
              to="/apps"
              size="sm"
            >
              View All
            </Button>
          </Group>

          {appsLoading ? (
            <Loader size="xl" type="dots" mx="auto" />
          ) : (
            <SimpleGrid cols={{ base: 1, md: 3 }}>
              {apps?.map((app: any) => (
                <AppCard key={app.id} {...app} />
              ))}
            </SimpleGrid>
          )}

          <Group justify="space-between" align="flex-end" mt="md">
            <Stack gap={2}>
              <Title order={3}>Recent Error Reports</Title>
              <Text size="sm" c="dimmed">Latest bug submissions across all apps.</Text>
            </Stack>
            <Button
              variant="subtle"
              color="brand-blue"
              rightSection={<TbChevronRight size={16} />}
              component={Link}
              to="/bug-reports"
              size="sm"
            >
              View All
            </Button>
          </Group>

          <Paper withBorder radius="2xl" className="glass" p="md" style={{ overflowX: 'auto' }}>
            <Table className="data-table" verticalSpacing="sm" style={{ minWidth: 560 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>App</Table.Th>
                  <Table.Th>Error Message</Table.Th>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Version</Table.Th>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Reported</Table.Th>
                  <Table.Th style={{ whiteSpace: 'nowrap' }}>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentErrorsLoading ? (
                  <Table.Tr>
                    <Table.Td colSpan={5} align="center" py="xl">
                      <Loader size="sm" type="dots" />
                    </Table.Td>
                  </Table.Tr>
                ) : recentErrors.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Stack align="center" gap="xs" py="xl">
                        <TbAlertCircle size={32} style={{ opacity: 0.25 }} />
                        <Text c="dimmed" size="sm">No error reports yet — all systems are running smoothly.</Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : recentErrors.map((error: any) => (
                  <Table.Tr key={error.id}>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Text fw={600} size="sm" tt="uppercase">{error.app}</Text>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 280 }}>
                      <Tooltip label={error.message} multiline maw={320} withArrow position="top-start">
                        <Text size="sm" c="dimmed" lineClamp={1} style={{ cursor: 'default' }}>
                          {error.message}
                        </Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Badge variant="light" color="gray" size="sm">v{error.version}</Badge>
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Text size="xs" c="dimmed">{formatTimeAgo(error.time)}</Text>
                    </Table.Td>
                    <Table.Td style={{ whiteSpace: 'nowrap' }}>
                      <Badge
                        color={SEVERITY_COLOR[error.severity] ?? 'gray'}
                        variant="light"
                        size="sm"
                        tt="capitalize"
                      >
                        {formatSeverityLabel(error.severity)}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Stack>
      </Container>
    </DashboardLayout>
  )
}
